"use client"

import AccessDenied from "@/components/custom-access-denied"
import { useCheckPermission } from "@/hook/use-check-permission"
import { useGuardedForm } from "@/hook/useGuardedForm"
import { useSuppliers } from "@/hook/useSuppliers"
import { t } from "@/languages/i18n"
import { SupllierRequest } from "@/types/codegen/data-contracts"
import { createPath } from "@/utils/create-path"
import { formatContentToHTML } from "@/utils/format-content-to-html"
import { normalizeFormData } from "@/utils/normalize-form-data"
import { FormSection, FormShell, FormSkeleton, StickyActionBar } from "@/(admin)/components/form"
import { CheckCircleOutlined, PlusCircleOutlined } from "@ant-design/icons"
import { Button, Form } from "antd"
import { useRouter, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useState } from "react"
import { SUPPLIERS } from "../../../../constants/admin-paths"
import SupplierFormFields from "../components/SupplierFormFields"

interface FormUpsertSupplierProps {
    onSupplierAdded?: () => void
}

const FormUpsertSupplier: React.FC<FormUpsertSupplierProps> = ({ onSupplierAdded }) => {
    const searchParams = useSearchParams()
    const id = searchParams.get("id")
    const router = useRouter()
    const isEditMode = Boolean(id)
    const [form] = Form.useForm()
    const [isReady, setIsReady] = useState(false);
    const { hasPermission: checkPermission } = useCheckPermission()
    const [isFormReady, setIsFormReady] = useState(false);
    const {
        loading, supplierDetail,
        actions: { getSupplierInformation, updateSupplier, createSupplier, hasPermission }
    } = useSuppliers()

    const getNormalizedSnapshot = useCallback(() => {
        const values = form.getFieldsValue(true)
        const rawSnapshot = {
            fullname: values.fullname || "",
            position: values.position || "",
            companyName: values.companyName || "",
            ico: values.ico || "",
            dic: values.dic || "",
            phoneNumber: values.phoneNumber || "",
            email: values.email || "",
            companyAddress: values.companyAddress || "",
            note: values.note || "",
            resolved: values.resolved ?? false,
        }
        return normalizeFormData(rawSnapshot)
    }, [form])

    const { isChange, checkChange, commitSnapshot, message, modal, handleCancel: guardedHandleCancel } = useGuardedForm({
        form,
        getSnapshot: getNormalizedSnapshot
    })

    // Check permission to access the page
    if (!checkPermission("supllier", isEditMode ? "update" : "create")) {
        return <AccessDenied />
    }

    useEffect(() => {
        if (isEditMode && supplierDetail) {
            const attr = supplierDetail?.attributes

            form.setFieldsValue({
                ...attr,
                note: formatContentToHTML(attr?.note)
            })
            setIsReady(true);
            setIsFormReady(true);
        }
    }, [supplierDetail, isEditMode])

    const handleSubmit = async () => {
        try {
            const values = await form.getFieldsValue()

            const requestData: SupllierRequest = {
                data: {
                    fullname: values.fullname,
                    position: values.position,
                    companyName: values.companyName,
                    ico: values.ico,
                    dic: values.dic,
                    phoneNumber: values.phoneNumber,
                    email: values.email,
                    companyAddress: values.companyAddress,
                    note: values.note,
                    resolved: values.resolved,
                }
            }

            if (isEditMode) {
                await updateSupplier(Number(id), requestData)
                commitSnapshot()
            } else {
                const success = await createSupplier(requestData)
                if (success) {
                    commitSnapshot()
                    onSupplierAdded?.()
                    router.push(createPath(SUPPLIERS.LIST.PATH, searchParams))
                }
            }
        } catch (error) {
            console.error('Submit error:', error)
        }
    }

    useEffect(() => {
        if (id && isEditMode) {
            getSupplierInformation(Number(id))
        }
        else {
            setIsReady(true);
            setIsFormReady(true);
        }
    }, [id, isEditMode])

    useEffect(() => {
        if (isFormReady) {
            const animationId = requestAnimationFrame(() => {
                commitSnapshot();
            });
            return () => cancelAnimationFrame(animationId);
        }
    }, [isFormReady, commitSnapshot]);

    const handleCancel = () => router.push(`/admin/${SUPPLIERS.LIST.PATH}`)
    const canSubmit = isEditMode ? hasPermission("update") : hasPermission("create");

    if (isEditMode && !isFormReady) {
        return <FormSkeleton mainSections={2} sidebarCards={0} />
    }

    const crumbs = [
        { title: t("Nhà cung cấp"), href: createPath(SUPPLIERS.LIST.PATH, searchParams) },
        { title: isEditMode ? t("Chỉnh sửa") : t("Tạo mới") },
    ]

    const main = (
        <FormSection title={t("Thông tin nhà cung cấp")} collapsible={false}>
            <SupplierFormFields form={form} isReady={isReady} onValuesChange={checkChange} />
        </FormSection>
    )

    const actionBar = (
        <StickyActionBar
            right={
                <>
                    <Button onClick={handleCancel}>{t("Hủy")}</Button>
                    <Button
                        type="primary"
                        icon={isEditMode ? <CheckCircleOutlined /> : <PlusCircleOutlined />}
                        onClick={() => form.submit()}
                        loading={loading}
                        disabled={loading || !canSubmit}
                    >
                        {isEditMode ? t("Cập nhật") : t("Thêm mới")}
                    </Button>
                </>
            }
        />
    )

    return (
        <Form labelAlign="left" layout="vertical" form={form} onFinish={handleSubmit} onValuesChange={checkChange}>
            <FormShell
                title={isEditMode ? t("Cập nhật nhà cung cấp") : t("Thêm mới nhà cung cấp")}
                crumbs={crumbs}
                main={main}
                actionBar={actionBar}
            />
        </Form>
    );
}

export default FormUpsertSupplier;